import Page from 'UIKit/Page'
import { ALL_GAME_TAG } from 'config/constants/gameTags'
import { RouteConfig } from 'config/constants/route'
import { useRouter } from 'hooks/useRouter'
import { RowMiddle } from 'layout/Components/Row'
import React, { useCallback, useMemo, useState } from 'react'
import GameService from 'services/GameService'
import { useAppSelector } from 'state'
import styled from 'styled-components'
import { formatRoute } from 'utils/stringHelper'

import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Search from 'UIKit/Search'
import Text from 'UIKit/Text/Text'
import ProviderFilter from 'components/ProviderFilter'
import useDebounce from 'hooks/useDebounce'
import { Trans, useTranslation } from 'react-i18next'
import GameList from './components/GameList'
import GameSubTag from './components/GameSubTag'
import GameTagGroup from './components/GameTagGroup'
import GamesFilter from './components/GamesFilter'
import { GameFilterBaseOptions, GameFilterOptionType } from './components/GamesFilter/config'
import useGamesFilter from './components/GamesFilter/useGamesFilter'
import { mapSortType } from './components/GamesFilter/utils'

const Games: React.FC<{ tagCode: string }> = ({ tagCode }) => {
  const router = useRouter()
  const defaultScreenTags = useAppSelector((state) => state.app.defaultScreenTags)
  const gameProviders = useAppSelector((state) => state.app.providers)

  const { t } = useTranslation()

  const gameTypeDefault = useMemo(
    () =>
      router.query.filterType
        ? GameFilterBaseOptions.find((option) => option.key === router.query.filterType)
        : GameFilterBaseOptions[0],
    [router.query.filterType],
  )

  const { selectedFilterType, onSelectOption } = useGamesFilter(gameTypeDefault)
  const { gameSortType, gameSortOrder } = mapSortType(selectedFilterType)

  // Query filter
  const [searchText, setSearchText] = useState('')
  const debouncedSearch = useDebounce(searchText, 600)

  // Tags filter
  const tags = useMemo(() => defaultScreenTags?.map((screenTag) => screenTag.tag) || [], [defaultScreenTags])
  const tag = tags.find((tag) => tag === tagCode?.toLowerCase()) || ALL_GAME_TAG

  // Subtags filter
  const subTags = useMemo(
    () => defaultScreenTags?.find((screenTag) => screenTag.tag === tag)?.childTags || [],
    [defaultScreenTags, tagCode],
  )
  const querySubTag = router.query.subTag?.toString() || ''
  const subTag = subTags.find((tag) => tag === querySubTag.toLowerCase()) || ALL_GAME_TAG

  // collection filter
  const gameCollectionId = useMemo(() => {
    const collectionId = Number(router.query.gameCollection) || 0
    return !Number.isNaN(collectionId) && !!collectionId && collectionId
  }, [router.query])

  // Providers filter
  const providers = useMemo(() => {
    const providers = router.query?.providers || []
    return gameProviders
      .map((provider) => (providers.includes(provider.code) ? provider.code : null))
      .filter((provider) => provider)
  }, [router.query?.providers, gameProviders])

  // handle filter
  const onSelectProviders = (providerCodes: string[]) => {
    handleChangeTag(tag, subTag, providerCodes)
  }

  const onSelectTag = (value: string) => {
    handleChangeTag(value, ALL_GAME_TAG, providers)
  }

  const onSelectSubTag = (value: string) => {
    handleChangeTag(tag, value, providers)
  }

  const handleSelectFilterType = (option: GameFilterOptionType) => {
    onSelectOption(option)
    handleChangeTag(tag, subTag, providers, option.key)
  }

  const handleChangeTag = (tag: string, subTag: string, providerCodes: string[], gameFilterType?: string) => {
    let query: any = {
      providers: providerCodes,
      filterType: gameFilterType || selectedFilterType?.key,
    }

    if (gameCollectionId) {
      query.gameCollection = gameCollectionId
    }

    if (subTag !== ALL_GAME_TAG) {
      query = { ...query, subTag }
    }

    let pathname = RouteConfig.Games.toString()
    if (tag !== ALL_GAME_TAG) {
      pathname = formatRoute(RouteConfig.GamesTag, { tag })
    }

    router.push(
      {
        pathname,
        query,
      },
      undefined,
      { scroll: false },
    )
  }

  const request = useCallback(
    (offset: number, limit: number) => {
      return GameService.getGames(
        {
          providerCodes: providers,
          tags: [tag, subTag],
          gameCollectionId,
          query: debouncedSearch,
          gameSortType,
          gameSortOrder,
        },
        offset,
        limit,
      )
    },
    [tag, subTag, providers, gameCollectionId, debouncedSearch, selectedFilterType?.key],
  )

  return (
    <Page position="relative">
      <Box>
        {gameCollectionId && (
          <Flex alignItems="center" justifyContent="space-between" mb="28px" flexWrap={['wrap', 'wrap', 'nowrap']}>
            <Text fontSize="20px" bold lineHeight="24px">
              <Trans>Bonus wagering games</Trans>
            </Text>

            <StyledSearch
              id="bonus-wagering-games-search-input"
              onValueChanged={setSearchText}
              value={searchText}
              width="100%"
              maxWidth={['100%', '', '320px']}
              mt={['24px', '', '0px']}
              placeholder={t('Search bonus wagering games')}
            />
          </Flex>
        )}
        <Flex />
        <GameTagGroup value={tag} tags={tags} onTagChanged={onSelectTag} />

        <Flex mt="20px" flexDirection={['column', 'column', 'column', 'row']} justifyContent="space-between">
          <GameSubTag tags={subTags} value={subTag} onTagChanged={onSelectSubTag} />
          <RowMiddle height="36px" mt={['16px', '', '', '0px']} justifyContent="flex-end">
            <GamesFilter
              filterOptions={GameFilterBaseOptions}
              selectedOption={selectedFilterType}
              onSelectOption={handleSelectFilterType}
              mr="8px"
              height="100%"
              width="auto"
            />
            <ProviderFilter
              selectedProviders={providers}
              onProvidersChanged={onSelectProviders}
              height="100%"
              width="auto"
            />
          </RowMiddle>
        </Flex>
      </Box>

      <GameList request={request} mt="20px" minHeight="var(--screen-height)" />
    </Page>
  )
}

const StyledSearch = styled(Search)`
  background: rgba(35, 44, 61, 0.4);

  height: 44px;
  border-radius: ${({ theme }) => theme.radii.extraLarge};
  border: unset !important;
`

export default Games
